// Presentation of server-owned evidence. A start timestamp never proves that
// processing is still underway; this clock measures time since the start only.
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TERMINAL_HISTORY_MS = 24 * 60 * 60 * 1000;
const STAGE_ORDER = { acquiring_data: 0, checking_session: 1, evaluating_symbols: 2,
  recording_result: 3, completed: 4, failed: 4 };

export const elapsedSinceRecordedStart = (bot, now) => {
  if (bot?.activity?.code !== 'evaluation_started') return null;
  if (!ISO_INSTANT.test(bot.responseAsOf) || !ISO_INSTANT.test(bot.activity.observed_at)) return null;
  const asOf = Date.parse(bot.responseAsOf);
  const start = Date.parse(bot.activity.observed_at);
  const receivedAt = bot.localFreshness?.receivedAt;
  const duration = bot.localFreshness?.duration;
  if (![asOf, start, receivedAt, duration, now].every(Number.isFinite)) return null;
  const serverAge = asOf - start;
  const localAge = now - receivedAt;
  if (serverAge < 0 || serverAge + localAge > 300000 || localAge < 0
      || localAge >= duration) return null;
  return Math.floor((serverAge + localAge) / 1000);
};

export const formatElapsed = (seconds) => {
  if (!Number.isInteger(seconds) || seconds < 0) return null;
  const minutes = Math.floor(seconds / 60);
  return minutes >= 60
    ? `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
    : `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
};

export const verifiedExecution = (bot, now) => {
  const execution = bot?.activity?.execution;
  if (!execution || !['running', 'completed', 'failed'].includes(execution.status)
      || !ISO_INSTANT.test(bot.responseAsOf) || !ISO_INSTANT.test(execution.started_at)
      || !ISO_INSTANT.test(execution.updated_at)
      || !Number.isInteger(execution.completed_symbols)
      || !Number.isInteger(execution.total_symbols)
      || execution.total_symbols < 1 || execution.total_symbols > 50
      || execution.completed_symbols < 0 || execution.completed_symbols > execution.total_symbols
      || !Array.isArray(execution.events) || execution.events.length > 12) return null;
  const asOf = Date.parse(bot.responseAsOf);
  const start = Date.parse(execution.started_at);
  const updated = Date.parse(execution.updated_at);
  const age = asOf - updated;
  if (![asOf, start, updated].every(Number.isFinite) || start > updated
      || age < 0 || age > (execution.status === 'running' ? 120000 : TERMINAL_HISTORY_MS)) return null;
  if (execution.status === 'running') {
    const receivedAt = bot.localFreshness?.receivedAt;
    const duration = bot.localFreshness?.duration;
    if (bot.activity.code !== 'evaluation_running' || bot.activity.worker_connected !== true
        || ![receivedAt, duration, now].every(Number.isFinite)
        || now - receivedAt < 0 || now - receivedAt >= duration) return null;
  }
  return execution;
};

// All steps come from one database row identified by execution_id. A bounded
// row may omit intermediate steps, so the UI describes retained steps only.
export const verifiedReplay = (bot, now) => {
  const execution = verifiedExecution(bot, now);
  if (!execution || !['completed', 'failed'].includes(execution.status)
      || ['evaluation_running', 'evaluation_started'].includes(bot?.activity?.code)
      || !UUID.test(execution.execution_id || '')
      || execution.stage !== execution.status
      || !ISO_INSTANT.test(execution.finished_at || '')
      || !execution.events.length) return null;
  const started = Date.parse(execution.started_at);
  const finished = Date.parse(execution.finished_at);
  if (!Number.isFinite(finished) || finished < started
      || finished !== Date.parse(execution.updated_at)
      || (execution.status === 'completed'
          && execution.completed_symbols !== execution.total_symbols)) return null;
  let previousTime = started;
  let previousCount = 0;
  let previousStage = -1;
  for (const [index, event] of execution.events.entries()) {
    if (!event || !Object.hasOwn(STAGE_ORDER, event.stage)
        || !ISO_INSTANT.test(event.at || '')
        || !Number.isInteger(event.completed_symbols)) return null;
    const at = Date.parse(event.at);
    if (!Number.isFinite(at) || at < previousTime || at > finished
        || event.completed_symbols < previousCount
        || event.completed_symbols > execution.total_symbols
        || STAGE_ORDER[event.stage] < previousStage
        || (['completed', 'failed'].includes(event.stage)
            && index !== execution.events.length - 1)) return null;
    previousTime = at;
    previousCount = event.completed_symbols;
    previousStage = STAGE_ORDER[event.stage];
  }
  const last = execution.events.at(-1);
  if (last.stage !== execution.status || previousTime !== finished
      || previousCount !== execution.completed_symbols) return null;
  return execution;
};

export const replayStorageKey = (botId, executionId) =>
  `pinngro:verified-replay:${botId}:${executionId}`;

export const replayStepDelay = (eventCount) =>
  Math.max(650, Math.ceil(4000 / eventCount));

export const activityPollInterval = (bots, singleBot, count, now) => {
  const active = Array.isArray(bots) && bots.some((bot) => {
    if (bot?.activity?.code === 'evaluation_running') {
      return Boolean(verifiedExecution(bot, now));
    }
    if (bot?.activity?.code !== 'evaluation_started'
        || !ISO_INSTANT.test(bot.responseAsOf || '')
        || !ISO_INSTANT.test(bot.activity.observed_at || '')) return false;
    const age = Date.parse(bot.responseAsOf) - Date.parse(bot.activity.observed_at);
    return Number.isFinite(age) && age >= 0 && age <= 120000;
  });
  if (active) return singleBot ? 2000 : 3000;
  return singleBot ? 15000 : count > 25 ? 30000 : 20000;
};

export const executionElapsed = (bot, now) => {
  const execution = verifiedExecution(bot, now);
  if (!execution || execution.status !== 'running') return null;
  const seconds = (Date.parse(bot.responseAsOf) - Date.parse(execution.started_at)
    + now - bot.localFreshness.receivedAt) / 1000;
  return seconds >= 0 ? Math.floor(seconds) : null;
};

export const verifiedRecentResult = (bot) => {
  const result = bot?.activity?.recent_result;
  if (!result || !['completed', 'failed'].includes(result.kind)
      || bot.activity.code === 'evaluation_started') return null;
  if (!ISO_INSTANT.test(bot.responseAsOf) || !ISO_INSTANT.test(result.at)) return null;
  const age = Date.parse(bot.responseAsOf) - Date.parse(result.at);
  return Number.isFinite(age) && age >= 0 && age <= 300000 ? result : null;
};

export const recentResultCue = (bot) => {
  if (bot?.activity?.worker_connected !== true) return null;
  const result = verifiedRecentResult(bot);
  if (!result) return null;
  const age = Date.parse(bot.responseAsOf) - Date.parse(result.at);
  return age <= 15000 ? { kind: result.kind, key: `${bot.bot_id}:${result.kind}:${result.at}` } : null;
};
